import { documentStore, uiStore } from "@/store/store";
import { getDomElementFromTreeNode } from "@/lib/canvas/helpers";
import { forceNextTick } from "@/util/force-next-tick";
import { TreeNode } from "@/types/tree";
import { runCallbackIfElementIsNotVisibleInRoot } from "@/util/dom";
import { getBreakdanceHeaderPostType } from "@/lib/constants/post-types";
import { isFutureLayerMode } from "@/futurelayer/ui/ui";
import { SECTION_SLUG } from "../elements/constants";
import { findNodeParent } from "@/lib/tree/tree";

let previousActiveElement: TreeNode | false = false;

function shouldSkipScrollingDueToPreviousElement(): boolean {
  if (!isFutureLayerMode() || !previousActiveElement) {
    return false;
  }

  const currentElement = uiStore.activeElement;
  if (!currentElement) return false;
  
  // Don't scroll if the new active element is the same as previous
  if (currentElement.id === previousActiveElement.id) {
    previousActiveElement = currentElement;
    return true;
  }
  
  // Don't scroll if the previous active element was a child (or descendant) of the new active element
  let ancestor = findNodeParent(documentStore.document.tree, previousActiveElement);
  while (ancestor) {
    if (ancestor.id === currentElement.id) {
      previousActiveElement = currentElement;
      return true;
    }
    ancestor = findNodeParent(documentStore.document.tree, ancestor);
  }

  return false;
}

export function scrollToActiveElement(disableSkipping = false) {
  // An element can be "activated" before it's visible on the page (like when we duplicate big elements)
  // Scroll to it after it's visible
  forceNextTick(() => {
    if (
      !uiStore.settings.autoScrollEnabled ||
      uiStore.isCanvasElementBeingDragged ||
      !uiStore.activeElement
    ) {
      return;
    }

    if (
      documentStore.document.documentMeta.postType ===
      getBreakdanceHeaderPostType()
    ) {
      return;
    }

    /**
       Chrome has a bug where only one scrollIntoView can be used at the same time when it has "smooth".
       If it's instant, multiple can be used, but we want the canvas scroll to be smooth, so we use "scrollTo" instead

       https://stackoverflow.com/questions/49318497/google-chrome-simultaneously-smooth-scrollintoview-with-more-elements-doesn
       https://bugs.chromium.org/p/chromium/issues/detail?id=833617
       */

    // Check if we should skip scrolling due to previous active element relationship
    if (!disableSkipping && shouldSkipScrollingDueToPreviousElement()) {
      return;
    }
  
    const futureLayerSectionBehavior = isFutureLayerMode() && uiStore.activeElement.data.type === SECTION_SLUG ? true : false;
    
    // Store the current active element as previous for next time
    previousActiveElement = uiStore.activeElement;
    
    smoothScrollToElementOnCanvas(uiStore.activeElement, futureLayerSectionBehavior);
  });
}

export function smoothScrollToLastElement() {
  const children = documentStore.document.tree.root.children;
  const lastSection = children[children.length - 1];
  smoothScrollToElementOnCanvas(lastSection);
}

export function smoothScrollToElementOnCanvas(element: TreeNode, futureLayerSectionBehavior?: boolean) {
  const domElement = getDomElementFromTreeNode(element);

  if (!domElement) return;

  const smoothScrollToActiveElementOnCanvas = () => {
    const iframe = document.getElementById("iframe") as HTMLIFrameElement;
    const elementRect = domElement.getBoundingClientRect();
    const canvasHeight =
      window.Breakdance.canvas.document.defaultView?.innerHeight ||
      window.Breakdance.canvas.document.documentElement.clientHeight;
    const elementHeightPlusTitlebar = elementRect.height + 20;
    const pageOffsetY =
      window.Breakdance.canvas.document.defaultView?.pageYOffset || 0;

    const elementTopPosition = pageOffsetY + elementRect.top;

    // Don't scroll if element spans beyond both edges of viewport
    if (futureLayerSectionBehavior && elementRect.top < 0 && elementRect.bottom > canvasHeight) {
      return;
    }

    // Don't scroll if the whole element is visible in viewport
    if (futureLayerSectionBehavior && elementRect.top > 0 && elementRect.bottom < canvasHeight) {
      return;
    }

    // Don't scroll if element bottom is between 70% and 100% of viewport
    // if (futureLayerSectionBehavior && elementRect.bottom >= canvasHeight * 0.7 && elementRect.bottom <= canvasHeight) {
    //   return;
    // }

    let scrollTopPosition: number;

    if (futureLayerSectionBehavior) { // force scroll to top
      // Position element top 50px from viewport top
      scrollTopPosition = elementTopPosition - 50;
    } else {
      const isElementBiggerThanTheCanvas =
        elementHeightPlusTitlebar > canvasHeight;

      const elementCenteredPosition =
        elementTopPosition +
        // center the element
        (elementHeightPlusTitlebar - canvasHeight) / 2;

      scrollTopPosition = isElementBiggerThanTheCanvas
        ? // scroll to the top (minus the titlebar so it shows at the top)
          elementTopPosition - 40
        : elementCenteredPosition;
    }

    iframe.contentWindow?.scrollTo({
      top: scrollTopPosition,
      behavior: "smooth"
    });
  };

  if (futureLayerSectionBehavior) {
    // When forcing scroll to top, always scroll regardless of visibility
    smoothScrollToActiveElementOnCanvas();
  } else {
    // Use existing behavior - only scroll if element is not visible
    runCallbackIfElementIsNotVisibleInRoot(
      smoothScrollToActiveElementOnCanvas,
      domElement,
      /**
       * root: null is the default, I'm being explicit because the Canvas is an iframe
       * and IntersectionObserver will, by default, use  the "viewport" of the page
       * If an element is inside an iframe then it'll use the Iframe's "viewport"
       * *https://github.com/w3c/IntersectionObserver/issues/372#issuecomment-543938684
       **/
      { root: null }
    );
  }
}
